import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  PageHeader,
  StatCard,
  Card,
  SectionHeader,
  Table,
  Badge,
  StatusDot,
  ProgressBar,
  Spinner,
  QueryError,
} from "../components";
import { usePolling } from "../hooks/usePolling";
import { apiGet } from "../lib/api";
import {
  formatDateOnly,
  formatDateTime,
  formatFleetHealthReason,
  formatFleetRecommendedAction,
  formatFleetStatusLabel,
} from "../lib/display";
// Bucket C Round 2 — canonical fleet-overview wire shape. Type-only so Vite
// never resolves zod at runtime.
import type { FleetOverviewResponse } from "../../../../src/shared/contracts/fleet-overview.js";

interface AlertSummary {
  critical: number;
  warning: number;
  info: number;
  total: number;
  open: number;
}

interface Deployment {
  id: string;
  tenantId: string;
  tenantName: string;
  status: string;
  health: "healthy" | "degraded" | "unknown" | "unhealthy" | "offline";
  healthReason?: string | null;
  healthStateSince?: number | string | null;
  recommendedAction?: string | null;
  version: string;
  // Bucket C Round 2 (D4 §5) — per-component tags. Non-null on post-D
  // deployments; all three null on pre-D legacy rows.
  gatewayTag: string | null;
  hankTag: string | null;
  pluginsBundleTag: string | null;
  uptime: number;
  activeSessions: number;
  episodesToday: number;
  gatewayUrl: string;
  hankUrl: string | null;
  adminUrl: string | null;
  lastHealthAt: number | string | null;
  createdAt: number | string;
}

interface FleetTask {
  id: string;
  type: string;
  deploymentId: string;
  tenantName: string;
  description: string;
  status: "pending" | "in_progress" | "completed" | "blocked" | "failed";
  priority: "critical" | "high" | "medium" | "low";
  createdAt: string;
}

interface ActiveUpgrade {
  id: string;
  fromVersion: string;
  toVersion: string;
  strategy: string;
  currentBatch: number;
  totalBatches: number;
  tenantsUpgraded: number;
  tenantsTotal: number;
  healthGate: { status: string; errorRate: number; threshold: number };
  status: string;
}

const STATUS_DOT_MAP: Record<string, "healthy" | "degraded" | "down"> = {
  healthy: "healthy",
  degraded: "degraded",
  offline: "down",
  unhealthy: "down",
  unknown: "degraded",
};

const STATUS_BORDER_MAP: Record<string, string> = {
  healthy: "var(--c-success)",
  degraded: "var(--c-warning)",
  offline: "var(--c-danger)",
  unhealthy: "var(--c-danger)",
  unknown: "var(--c-border)",
};

const PRIORITY_COLOR_MAP: Record<string, "red" | "amber" | "gray"> = {
  critical: "red",
  high: "amber",
  medium: "gray",
  low: "gray",
};

const STATUS_BADGE_COLOR_MAP: Record<string, "blue" | "amber" | "green" | "red"> = {
  pending: "blue",
  in_progress: "amber",
  completed: "green",
  blocked: "red",
  failed: "red",
};

export default function FleetOverview() {
  const {
    data: overview,
    isLoading: loadingOverview,
    error: errorOverview,
  } = useQuery<FleetOverviewResponse>({
    queryKey: ["fleet", "overview"],
    queryFn: () => apiGet<FleetOverviewResponse>("/api/fleet/overview"),
  });

  const { data: deployments, isLoading: loadingDeployments } = useQuery<Deployment[]>({
    queryKey: ["fleet", "deployments"],
    queryFn: async () => {
      const data = await apiGet<Deployment[] | { deployments?: Deployment[] }>(
        "/api/fleet/deployments",
      );
      return Array.isArray(data) ? data : (data.deployments ?? []);
    },
  });

  const { data: tasks, isLoading: loadingTasks } = useQuery<FleetTask[]>({
    queryKey: ["fleet", "tasks"],
    queryFn: async () => {
      const data = await apiGet<FleetTask[] | { tasks?: FleetTask[] }>("/api/fleet/tasks");
      return Array.isArray(data) ? data : (data.tasks ?? []);
    },
  });

  const { data: activeUpgrade } = useQuery<ActiveUpgrade | null>({
    queryKey: ["fleet", "upgrade", "active"],
    queryFn: () => apiGet<ActiveUpgrade | null>("/api/fleet/upgrade/active"),
  });

  const { data: alertSummary } = useQuery<AlertSummary>({
    queryKey: ["alerts-summary"],
    queryFn: () => apiGet<AlertSummary>("/api/alerts/summary"),
  });

  usePolling(
    [
      ["fleet", "overview"],
      ["fleet", "deployments"],
      ["fleet", "tasks"],
      ["fleet", "upgrade", "active"],
      ["alerts-summary"],
    ],
    30_000,
  );

  const isLoading = loadingOverview || loadingDeployments || loadingTasks;

  if (isLoading && !overview) {
    return (
      <div>
        <PageHeader title="Fleet Overview" subtitle="Monitor your deployment fleet" />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (errorOverview && !overview) {
    return (
      <div>
        <PageHeader title="Fleet Overview" subtitle="Monitor your deployment fleet" />
        <QueryError
          error={errorOverview}
          queryKeys={[
            ["fleet", "overview"],
            ["fleet", "deployments"],
            ["fleet", "tasks"],
          ]}
        />
      </div>
    );
  }

  const taskColumns = [
    {
      key: "priority",
      header: "Priority",
      render: (row: FleetTask) => (
        <Badge color={PRIORITY_COLOR_MAP[row.priority]} dot>
          {row.priority}
        </Badge>
      ),
    },
    {
      key: "type",
      header: "Type",
      render: (row: FleetTask) => (
        <span
          style={{ color: "var(--c-text)", fontWeight: 500, textTransform: "capitalize" as const }}
        >
          {row.type}
        </span>
      ),
    },
    { key: "tenantName", header: "Tenant" },
    { key: "description", header: "Description" },
    {
      key: "status",
      header: "Status",
      render: (row: FleetTask) => (
        <Badge color={STATUS_BADGE_COLOR_MAP[row.status]}>{row.status.replace("_", " ")}</Badge>
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      render: (row: FleetTask) => formatDateOnly(row.createdAt),
    },
  ];

  const gateColor =
    activeUpgrade?.healthGate?.status === "passing" ? "var(--c-success)" : "var(--c-danger)";
  const upgradePct = activeUpgrade
    ? Math.round((activeUpgrade.tenantsUpgraded / activeUpgrade.tenantsTotal) * 100)
    : 0;

  return (
    <div>
      <PageHeader title="Fleet Overview" subtitle="Monitor your deployment fleet" />

      {/* Active upgrade banner */}
      {activeUpgrade && activeUpgrade.status === "in_progress" && (
        <div
          className="mb-6 rounded-lg p-4"
          style={{
            background: "var(--c-surface-2)",
            border: "1px solid var(--c-border)",
          }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <Badge color="amber">{activeUpgrade.strategy}</Badge>
              <span className="text-sm font-semibold" style={{ color: "var(--c-text)" }}>
                {activeUpgrade.fromVersion} → {activeUpgrade.toVersion}
              </span>
              <span className="text-xs" style={{ color: "var(--c-text-secondary)" }}>
                {activeUpgrade.tenantsUpgraded}/{activeUpgrade.tenantsTotal} tenants
              </span>
            </div>
            <span
              className="text-xs font-medium px-2 py-0.5 rounded"
              style={{ background: gateColor, color: "#fff" }}
            >
              Gate: {activeUpgrade.healthGate.status.toUpperCase()}
            </span>
          </div>
          <ProgressBar value={upgradePct} color="blue" />
          <div className="text-[11px] mt-1" style={{ color: "var(--c-text-faint)" }}>
            Batch {activeUpgrade.currentBatch} of {activeUpgrade.totalBatches} · Error rate:{" "}
            {activeUpgrade.healthGate.errorRate}% (threshold: {activeUpgrade.healthGate.threshold}%)
          </div>
          <div className="text-[11px] mt-2" style={{ color: "var(--c-text-faint)" }}>
            Fleet-wide upgrade controls stay hidden from the main operator UI until rollout
            orchestration is fully operational.
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard title="Total Deployments" value={String(overview?.totalDeployments ?? 0)} />
        <StatCard title="Active Sessions" value={String(overview?.totalAgentSessions ?? 0)} />
        <StatCard
          title="Fleet Uptime"
          value={(overview?.avgUptime ?? 0) + "%"}
          color="var(--c-success)"
        />
        <StatCard
          title="Monthly Cost"
          value={"$" + (overview?.totalCostThisMonth ?? 0).toLocaleString()}
        />
      </div>

      {/* Active alerts summary */}
      {alertSummary && alertSummary.open > 0 && (
        <>
          <div className="flex items-center justify-between mb-2">
            <SectionHeader title="Active Alerts" />
            <Link
              to="/actions"
              style={{
                fontSize: 13,
                color: "var(--c-primary)",
                textDecoration: "none",
              }}
            >
              View Action Center
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <StatCard
              title="Critical"
              value={String(alertSummary.critical)}
              color={alertSummary.critical > 0 ? "var(--c-danger)" : undefined}
            />
            <StatCard
              title="Warning"
              value={String(alertSummary.warning)}
              color={alertSummary.warning > 0 ? "var(--c-warning)" : undefined}
            />
            <StatCard title="Info" value={String(alertSummary.info)} />
          </div>
        </>
      )}

      {/* Deployment health grid */}
      <SectionHeader
        title="Deployment Health"
        subtitle={`${deployments?.length ?? 0} deployments`}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {(deployments ?? []).map((dep) => (
          <Link
            key={dep.id}
            to={`/deployments/${dep.id}`}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <Card style={{ cursor: "pointer", transition: "box-shadow 0.15s ease" }}>
              <div
                style={{
                  borderLeft: `3px solid ${STATUS_BORDER_MAP[dep.health]}`,
                  paddingLeft: 12,
                  marginLeft: -20,
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <StatusDot status={STATUS_DOT_MAP[dep.health]} />
                  <span className="text-sm font-medium" style={{ color: "var(--c-text)" }}>
                    {dep.tenantName}
                  </span>
                  <Badge
                    color={
                      dep.health === "healthy"
                        ? "green"
                        : dep.health === "degraded"
                          ? "amber"
                          : "red"
                    }
                  >
                    {formatFleetStatusLabel(dep.health)}
                  </Badge>
                </div>
                <div
                  className="space-y-2 mb-3 text-xs"
                  style={{ color: "var(--c-text-secondary)" }}
                >
                  <div
                    className="rounded-md px-2 py-2"
                    style={{ backgroundColor: "var(--c-surface-2)", color: "var(--c-text)" }}
                  >
                    <div className="font-medium">{formatFleetHealthReason(dep.healthReason)}</div>
                    <div className="mt-1" style={{ color: "var(--c-text-secondary)" }}>
                      {formatFleetRecommendedAction(dep.recommendedAction)}
                    </div>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span>Version</span>
                    <code style={{ color: "var(--c-text-faint)" }}>{dep.version}</code>
                  </div>
                  {dep.gatewayTag !== null ||
                  dep.hankTag !== null ||
                  dep.pluginsBundleTag !== null ? (
                    <div style={{ color: "var(--c-text-faint)", fontSize: "0.75rem" }}>
                      gateway {dep.gatewayTag ?? "—"} · hank {dep.hankTag ?? "—"} · plugins{" "}
                      {dep.pluginsBundleTag ?? "—"}
                    </div>
                  ) : (
                    <div style={{ color: "var(--c-text-faint)", fontSize: "0.75rem" }}>
                      (legacy aggregate — pre-D)
                    </div>
                  )}
                  <div className="flex justify-between gap-2">
                    <span>Uptime</span>
                    <span style={{ color: "var(--c-text-faint)" }}>{dep.uptime}%</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span>Episodes today</span>
                    <span style={{ color: "var(--c-text-faint)" }}>{dep.episodesToday}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span>Status since</span>
                    <span style={{ color: "var(--c-text-faint)" }}>
                      {formatDateTime(dep.healthStateSince, "unknown")}
                    </span>
                  </div>
                </div>
                <div className="text-xs" style={{ color: "var(--c-text-secondary)" }}>
                  {dep.activeSessions} active session{dep.activeSessions !== 1 ? "s" : ""}
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent tasks table */}
      <SectionHeader title="Recent Tasks" />
      <Card padding={false}>
        <Table
          columns={taskColumns}
          data={(tasks ?? []) as unknown as Record<string, unknown>[]}
          keyField="id"
          loading={loadingTasks}
          emptyMessage="No tasks"
        />
      </Card>
    </div>
  );
}
